import {
  ChangeEventHandler,
  CSSProperties,
  FC,
  FocusEventHandler,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FiChevronDown } from 'react-icons/fi';
import { BiSubdirectoryRight } from 'react-icons/bi';
import { createPortal } from 'react-dom';

import isValuesEqual from 'lodash/isEqual';
import { useEventListener } from '@chakra-ui/react';

import useDebounce from '~/shared/hooks/useDebounce';
import { useClickEsc } from '~/shared/hooks/useClickEsc';

import { withIsMounted } from '~/shared/utils/withIsMounted';
import { normalizeString } from '~/shared/utils/normalizeString';

import { IMarginProps } from '~/shared/interfaces/IMarginProps';
import {
  ISelectOption,
  TSelectPosition,
  TSelectSize,
  TSelectValueType,
} from './types';

import {
  Container,
  InputWrapper,
  Dropdown,
  Option,
  IconWrapper,
  Value,
  NoOptions,
  DropdownContainer,
} from './styles';

import { Label } from '../Label';
import { ErrorMessage } from '../ErrorMessage';

export type TOnChangeSelect<TOption = ISelectOption | string | number> = (
  option: TOption
) => void;

export interface ISelectProps extends IMarginProps {
  onChange?: TOnChangeSelect;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  value?: string | ISelectOption;
  defaultValue?: string | ISelectOption;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  options: ISelectOption[];
  label?: string;
  size?: TSelectSize;
  error?: string;
  noOptionsText?: string;
  valueType?: TSelectValueType;
  scrollViewId?: string;
  containerStyle?: CSSProperties;
  name?: string;
  isPlaceholderOptionDisabled?: boolean;
}

const BaseSelect: FC<ISelectProps> = withIsMounted(
  ({
    onChange = () => null,
    value: _value,
    options,
    placeholder = 'Selecione uma opção',
    m,
    ml,
    mr,
    mv,
    mh,
    mb,
    mt,
    label,
    size = 'md',
    error,
    defaultValue,
    noOptionsText,
    valueType = 'string',
    scrollViewId = 'main-element',
    containerStyle = {},
    name,
    isPlaceholderOptionDisabled = false,
    disabled,
  }) => {
    const id = useId();

    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const containerRef = useRef<HTMLLabelElement>(null);

    const [searchValue, setSearchValue] = useState('');
    const debouncedValue = useDebounce(searchValue, 250);
    const [clientRect, setClientRect] = useState<DOMRect>();
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [value, setValue] = useState(defaultValue);

    const [position, setPosition] = useState<TSelectPosition>('bottom');

    useEffect(() => {
      setValue(_value);
    }, [_value]);

    const handleSetClientRect = (): void => {
      const containerClientRect = containerRef.current?.getBoundingClientRect();

      const isAtScreen = containerClientRect.bottom + 216 <= window.innerHeight;

      setClientRect(containerClientRect);
      setPosition(isAtScreen ? 'bottom' : 'top');
    };

    const openDropdown = (): void => {
      if (isDropdownVisible || disabled) return;
      handleSetClientRect();
      setIsDropdownVisible(true);
      inputRef.current?.focus();
    };

    const closeDropdown = useCallback((): void => {
      if (!isDropdownVisible || disabled) return;
      setIsDropdownVisible(false);
      setSearchValue('');
    }, [disabled, isDropdownVisible]);

    const handleClickSelect = (): void => {
      isDropdownVisible ? closeDropdown() : openDropdown();
    };

    const handleSelectOption = useCallback(
      (option: ISelectOption): void => {
        onChange(valueType === 'string' ? option.value : option);
        closeDropdown();
      },
      [closeDropdown, onChange, valueType]
    );

    const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = (
      event
    ) => {
      setSearchValue(event.target.value);
    };

    const verifyValueIsActive = useCallback(
      (valueToVerify: string | number): boolean => {
        return (
          valueToVerify ===
          (typeof value === 'string' || typeof value === 'number'
            ? value
            : value?.value)
        );
      },
      [value]
    );

    const inputWrapperRef = useRef<HTMLDivElement>(null);

    const formattedOptions = useMemo(() => {
      if (debouncedValue) {
        const normalizedSearchValue = normalizeString(debouncedValue);

        const hasMatchRecursively = (option: ISelectOption): boolean => {
          const currentMatches =
            normalizeString(option.label).includes(normalizedSearchValue) ||
            normalizeString(option.description).includes(normalizedSearchValue);

          if (currentMatches) return true;

          if (option.options && option.options.length > 0) {
            return option.options.some(hasMatchRecursively);
          }

          return false;
        };

        const filterSuboptions = (
          optionsToFilter: ISelectOption[]
        ): ISelectOption[] => {
          return optionsToFilter.filter(hasMatchRecursively).map((option) => {
            if (option.options && option.options.length > 0) {
              const filteredChildren = filterSuboptions(option.options);
              return {
                ...option,
                options: filteredChildren,
              };
            }
            return option;
          });
        };

        return filterSuboptions(options);
      }

      return options;
    }, [options, debouncedValue]);

    const findLabelRecursively = useCallback(
      (
        optionsToSearch: ISelectOption[],
        valueToFind: string | number
      ): string | null => {
        for (const option of optionsToSearch) {
          if (option.value === valueToFind) return option.label;
          if (option.options?.length) {
            const found = findLabelRecursively(option.options, valueToFind);
            if (found) return found;
          }
        }
        return null;
      },
      []
    );

    const optionValue = useMemo(() => {
      if (typeof value === 'string' || typeof value === 'number') {
        return findLabelRecursively(options, value);
      }

      return value?.value || null;
    }, [findLabelRecursively, options, value]);

    useEffect(() => {
      handleSetClientRect();
    }, []);

    useEffect(() => {
      const handleMouseDown = (event: MouseEvent): void => {
        if (!isDropdownVisible) return;

        const isClickInsideSelect = containerRef.current?.contains(
          event.target as Node
        );

        const isClickInsideDropdown = dropdownRef.current?.contains(
          event.target as Node
        );

        if (!isClickInsideSelect && !isClickInsideDropdown) {
          closeDropdown();
        }
      };

      document.addEventListener('mousedown', handleMouseDown);

      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
      };
    }, [isDropdownVisible, closeDropdown]);

    useClickEsc(closeDropdown);

    useEventListener('resize', handleSetClientRect);
    useEventListener(
      'scroll',
      closeDropdown,
      document.getElementById(scrollViewId)
    );

    const renderOptions = useCallback(
      (optionsToRender: ISelectOption[], level = 1): JSX.Element[] => {
        return optionsToRender.map((option) => (
          <Fragment key={option.value}>
            <Option
              size={size}
              key={option.value}
              onClick={() => handleSelectOption(option)}
              id={`select-option-${option.value}`}
              {...(verifyValueIsActive(option.value) && {
                isActive: true,
                className: 'is-active',
              })}
              level={level}
            >
              {level > 1 && <BiSubdirectoryRight />}
              <div>
                <span>{option.label}</span>
                {option.description && <p>{option.description}</p>}
              </div>
            </Option>

            {renderOptions(option.options || [], level + 1)}
          </Fragment>
        ));
      },
      [handleSelectOption, size, verifyValueIsActive]
    );

    return (
      <Container
        m={m}
        mv={mv}
        mh={mh}
        mt={mt}
        mb={mb}
        ml={ml}
        mr={mr}
        htmlFor={id}
        ref={containerRef}
      >
        {!!label && (
          <Label mb="xxs" onClick={handleClickSelect}>
            {label}
          </Label>
        )}

        <InputWrapper
          hasError={!!error}
          size={size}
          ref={inputWrapperRef}
          style={containerStyle}
          isDisabled={disabled}
        >
          <input
            name={name}
            ref={inputRef}
            placeholder={placeholder}
            value={searchValue}
            onChange={handleChangeSearch}
            {...(!isDropdownVisible && { onClick: handleClickSelect })}
            disabled={disabled}
          />

          <Value
            size={size}
            onClick={handleClickSelect}
            isVisible={!isDropdownVisible && !!optionValue}
            isDisabled={disabled}
          >
            {optionValue}
          </Value>

          <IconWrapper
            size={size}
            onClick={handleClickSelect}
            isDropdownVisible={isDropdownVisible}
          >
            <FiChevronDown />
          </IconWrapper>

          <>
            {createPortal(
              <DropdownContainer
                isVisible={isDropdownVisible}
                selectClientRect={clientRect}
              >
                <Dropdown
                  isVisible={isDropdownVisible}
                  ref={dropdownRef}
                  selectClientRect={clientRect}
                  position={position}
                  size={size}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {formattedOptions?.length > 0 ? (
                    <>
                      <Option
                        size={size}
                        id="select-option-placeholder"
                        onClick={() =>
                          handleSelectOption({ label: placeholder, value: '' })
                        }
                        isPlaceholder
                        isDisabled={isPlaceholderOptionDisabled}
                        {...(verifyValueIsActive('') && {
                          isActive: true,
                          className: 'is-active',
                        })}
                      >
                        {placeholder}
                      </Option>

                      {renderOptions(formattedOptions)}
                    </>
                  ) : (
                    <NoOptions size={size} id="select-option-empty">
                      {noOptionsText || 'Sem opções!'}
                    </NoOptions>
                  )}
                </Dropdown>
              </DropdownContainer>,
              document.getElementById('portal-root') as Element
            )}
          </>
        </InputWrapper>

        <ErrorMessage mt="xxs">{error}</ErrorMessage>
      </Container>
    );
  }
);

export const Select = memo(BaseSelect, (prevProps, nextProps) => {
  return isValuesEqual(prevProps, nextProps);
});
